// Single source of truth for the portfolio gallery layout. Replaces the old CSS
// :nth-child table, which keyed each figure's size + position to its DOM order and
// broke whenever an image was added/removed/commented. Positions are computed here
// in JS, keyed by a stable `id` — edit the array and the field reflows; items that
// carry an `override` (authored in /portfolio#debug) keep their exact spot.
//
// "Design space": origin top-left, +x right, +y DOWN, pixels at scale = 1.
// Gallery.applyLayout() recenters on the period rect and flips Y into world space
// (origin = screen center, +y up), then multiplies by the responsive scale.

const CELL = 94.07; // module cell (px) — preserved from the original baked grid
const GAP = 14.03; // module gap (px)

// box size (px) for a tile spanning n cells: n*CELL + (n-1)*GAP  (== 108.1n − 14.03)
export const box = (n) => n * CELL + (n - 1) * GAP;

export const CONFIG = {
	cell: CELL,
	gap: GAP,
	rowWidth: 2400, // design px: a row fills to ~this before wrapping to the next
	jitterX: 220, // max extra random horizontal advance between tiles (airy scatter)
	jitterY: 160, // max extra random vertical advance between rows
	seed: 1337, // deterministic — identical scatter every load (no reshuffle)
};

// One entry per media, in any order — `id` is the stable key, NOT the position.
// w/h are cell spans (migrated 1:1 from the original :nth-child sizes). `override`
// is null until authored in the GUI, then { x, y, rotation, scale } in design space.
export const ITEMS = [
	{
		id: 'converse',
		src: '/images/portfolio/converse_promo.jpg',
		w: 4,
		h: 3,
		mode: 'contain',
		objectPosition: 'center',
		override: { x: -21.5, y: 475.4, rotation: 0, scale: 1 },
	},
	{
		id: 'opart1',
		src: '/images/art/opart1.jpg',
		w: 3,
		h: 4,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 544.7, y: 542.5, rotation: 0, scale: 1 },
	},
	{
		id: 'utkina',
		src: '/images/art/utkina-billboard.jpg',
		w: 6,
		h: 3,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 1265.4, y: 389.3, rotation: 0, scale: 1 },
	},
	{
		id: 'opart',
		src: '/images/art/opart.jpg',
		w: 3,
		h: 3,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 1827.7, y: 431.1, rotation: 0, scale: 1 },
	},
	{
		id: 'dmjeans',
		src: '/images/art/dm-jeans-card.jpg',
		w: 4,
		h: 7,
		mode: 'cover',
		objectPosition: 'top',
		override: { x: 371.3, y: 1323.9, rotation: 0, scale: 1 },
	},
	{
		id: 'visionary',
		src: '/images/art/visionary-lettering-web.jpg',
		w: 4,
		h: 5,
		mode: 'cover',
		objectPosition: 'bottom',
		override: { x: 1193.6, y: 1393.3, rotation: 0, scale: 1 },
	},
	{
		id: 'drz1',
		src: '/images/portfolio/drzDesktop1.jpg',
		w: 6,
		h: 4,
		mode: 'cover',
		objectPosition: 'left',
		override: { x: 1912.7, y: 1015.2, rotation: 0, scale: 0.73 },
	},
	{
		id: 'bizcard',
		src: '/images/art/averyano-business-card-front.jpg',
		w: 5,
		h: 5,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 528.7, y: 2022.6, rotation: 0, scale: 1 },
	},
	{
		id: 'prelab',
		src: '/images/portfolio/project-prelab.jpg',
		w: 4,
		h: 3,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 1250.2, y: 2127.2, rotation: 0, scale: 1 },
	},
	{
		id: 'drz2',
		src: '/images/portfolio/drzDesktop2.jpg',
		w: 3,
		h: 2,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 1807.4, y: 2293.5, rotation: 0, scale: 1 },
	},
	{
		id: 'ukraine',
		src: '/images/portfolio/project-ukraine.jpg',
		w: 4,
		h: 4,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 2355.2, y: 2312.5, rotation: 0, scale: 1 },
	},
	{
		id: 'dmlogo',
		src: '/images/art/dm-logo.mp4',
		isVideo: true,
		w: 4,
		h: 6,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 63.9, y: 3031.3, rotation: 0, scale: 1 },
	},
	{
		id: 'drz3',
		src: '/images/portfolio/drzDesktop3.jpg',
		w: 4,
		h: 3,
		mode: 'cover',
		objectPosition: 'center',
		override: { x: 671.5, y: 2866.3, rotation: 0, scale: 1 },
	},
	{
		id: 'onimask',
		src: '/images/portfolio/OniMask.jpg',
		w: 5,
		h: 3,
		mode: 'cover',
		objectPosition: 'right',
		override: { x: 1298.8, y: 2864.5, rotation: 0, scale: 1 },
	},
];

// deterministic PRNG so the scatter is identical every reload
function mulberry32(seed) {
	let a = seed >>> 0;
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

// Pure. items + config -> { placements: Map(id -> {x,y,w,h,rotation,scale}), period }.
// Baseline = jittered row-pack: loose & airy, and non-overlapping by construction —
// x only ever advances by (tileW + gap + jitter) so neighbours keep ≥ gap, and each
// tile's vertical wobble stays inside its row band (bands are ≥ gap apart). The
// tiling period is the field extent + gap, so the infinite wrap seams at exactly
// `gap`. Items with an `override` use it verbatim (curated GUI placement).
export function generateLayout(items, config = CONFIG) {
	const { gap, rowWidth, jitterX, jitterY, seed } = config;
	const rand = mulberry32(seed);
	const placements = new Map();

	// 1) flow (non-overridden) items into rows
	const flowed = items.filter((it) => !it.override);
	const rows = [];
	let row = [];
	let rowW = 0;
	flowed.forEach((it) => {
		const w = box(it.w);
		if (row.length && rowW + gap + w > rowWidth) {
			rows.push(row);
			row = [];
			rowW = 0;
		}
		rowW += (row.length ? gap : 0) + w;
		row.push(it);
	});
	if (row.length) rows.push(row);

	// 2) place row by row, tracking the field extent for the period
	let y = 0;
	rows.forEach((r) => {
		const bandH = Math.max(...r.map((it) => box(it.h)));
		let x = 0;
		r.forEach((it, i) => {
			if (i > 0) x += gap + rand() * jitterX; // monotonic → spacing always ≥ gap
			const w = box(it.w);
			const h = box(it.h);
			const ty = y + rand() * Math.max(0, bandH - h); // wobble inside the band
			placements.set(it.id, {
				x: x + w / 2,
				y: ty + h / 2,
				w,
				h,
				rotation: 0,
				scale: 1,
			});
			x += w;
		});
		y += bandH + gap + rand() * jitterY;
	});

	// 3) overridden items use their authored center + transform verbatim
	items
		.filter((it) => it.override)
		.forEach((it) => {
			const o = it.override;
			placements.set(it.id, {
				x: o.x,
				y: o.y,
				w: box(it.w),
				h: box(it.h),
				rotation: o.rotation || 0,
				scale: o.scale || 1,
			});
		});

	// period = bounding box over ALL placements (+gap), so the infinite wrap stays
	// seamless even when every item is overridden (a fully hand-authored layout).
	let maxR = 0;
	let maxB = 0;
	placements.forEach((p) => {
		maxR = Math.max(maxR, p.x + p.w / 2);
		maxB = Math.max(maxB, p.y + p.h / 2);
	});
	const period = { w: maxR + gap, h: maxB + gap };

	return { placements, period };
}
